/**
 * Zwei Zeilen einer Tabelle in Reihenfolge bringen — nach ihrem WERT, nicht
 * nach dem Text, der in der Zelle steht.
 *
 * ═══ WARUM ES DIESE DATEI GIBT (H1.5) ═══
 *
 * Die Filtertabelle sortierte den angezeigten Text alphabetisch. Für Namen
 * stimmt das, für ein Datum nicht: In der Disposition steht beim Ladedatum
 * „Mo., 24.08.26", und alphabetisch kommt zuerst der Wochentag dran —
 * Di. < Do. < Fr. < Mi. < Mo. Ein Klick auf „Ladedatum" ergab eine Liste, die
 * sortiert AUSSAH und es nicht war: Die Tour vom 20.08. stand hinter der vom
 * 29.09. Keine Meldung, nur eine Tour, die man übersieht.
 *
 * Deshalb trennt die Tabelle jetzt drei Dinge, die vorher eines waren:
 * was angezeigt wird (`zeige`), wonach gefiltert wird (`wert`, der Text, den
 * man sieht und tippt) und wonach sortiert wird (`sortierwert`). Ein Datum
 * sortiert als `JJJJ-MM-TT` oder als Zeitpunkt, ein Betrag als Zahl.
 *
 * ═══ LEER STEHT HINTEN — IN BEIDE RICHTUNGEN ═══
 *
 * Vorher war ein fehlender Betrag eine 0 und stand beim Aufsteigen VOR allen
 * echten Beträgen; beim Absteigen verschwand er hinten. Eine Lücke ist aber
 * kein kleiner Wert, sondern gar keiner. Wer nach Betrag sortiert, will die
 * Beträge sehen — die Touren ohne Preis stehen gesammelt darunter, egal in
 * welche Richtung. Dasselbe gilt für ein fehlendes Ladedatum.
 */

/** Wonach eine Zeile sortiert wird. `null` heißt: kein Wert. */
export type Sortierwert = string | number | null;

/** Leerer Text und keine Zahl (NaN) zählen wie `null`. */
function istLeer(wert: Sortierwert): boolean {
  return wert === null || wert === '' || (typeof wert === 'number' && Number.isNaN(wert));
}

/**
 * Vergleich für `Array.prototype.sort`. Leere Werte stehen immer hinten, auch
 * absteigend — deshalb wird die Richtung HIER angewendet und nicht durch
 * Umdrehen des Ergebnisses: Umgedreht stünden die Lücken vorne.
 */
export function sortierwerteVergleichen(
  x: Sortierwert,
  y: Sortierwert,
  absteigend: boolean,
): number {
  const xLeer = istLeer(x);
  const yLeer = istLeer(y);
  if (xLeer || yLeer) return xLeer === yLeer ? 0 : xLeer ? 1 : -1;

  let vergleich: number;
  if (typeof x === 'number' && typeof y === 'number') {
    // Nicht `x - y`: Infinity - Infinity ergäbe NaN, und eine Vergleichs-
    // funktion, die NaN liefert, bringt die ganze Sortierung durcheinander.
    vergleich = x < y ? -1 : x > y ? 1 : 0;
  } else if (typeof x === 'string' && typeof y === 'string') {
    vergleich = x.localeCompare(y, 'de');
  } else {
    // Gemischt kommt nur bei einer falsch angegebenen Spalte vor. Zahlen vor
    // Text, damit die Reihenfolge wenigstens immer dieselbe ist.
    vergleich = typeof x === 'number' ? -1 : 1;
  }
  return absteigend ? -vergleich : vergleich;
}

/**
 * Der Sortierwert einer Zahlenspalte, die nur ihren Text kennt.
 *
 * Gedacht für Spalten mit `zahl: true`, deren `wert` eine Zahl als Text
 * liefert (`String(betrag)`, also „1856.25" mit Punkt). Ein leerer Text ist
 * KEINE 0 — genau so stand ein fehlender Betrag zwischen den echten.
 */
export function zahlAusText(text: string): number | null {
  if (text.trim() === '') return null;
  const zahl = Number(text);
  return Number.isFinite(zahl) ? zahl : null;
}

/**
 * Der Sortierwert einer Tournummer „JJ-NNNN" (HJ-034): erst das Jahr, dann die
 * laufende Nummer, beide als Zahl.
 *
 * Alphabetisch stimmt das nur, solange alle gleich lang sind — „26-10000" stünde
 * unter „26-9999", und die höchste Nummer stünde mitten in der Liste. Keine
 * Nummer („folgt") ist kein Wert und steht damit in beide Richtungen hinten.
 */
export function nummerSortierwert(nummer: string | null): number | null {
  const teile = nummer?.match(/^(\d+)-(\d+)$/);
  return teile ? Number(teile[1]) * 1_000_000 + Number(teile[2]) : null;
}
